import { isTemplateSpan } from 'typescript';
import type { OrderItemType } from '~/constants/types/order-item';
import OrderItem from '~/models/order';

export default function useFacebookConversion() {
  
  /**
   * Send Facebook conversion event for a purchase
   */
  const sendPurchaseConversion = async(order: OrderItem, currency?: string) => {
    try {
      // Get Facebook click ID from cookies - check both _fbc and fbBackupCookie like original
      const fbcCookie = useCookie<string>('_fbc', { 
        maxAge: 3600 * 24 * 182, 
        path: '/', 
      });
      const fbBackupCookie = useCookie<string>('fbBackupCookie', { 
        maxAge: 3600 * 24 * 182, 
        path: '/', 
      });
      
      // Use _fbc cookie first, fallback to fbBackupCookie if _fbc is empty or doesn't exist
      let fbc = fbcCookie.value;
      if (!fbc || (fbc === '' && fbBackupCookie.value)) {
        fbc = fbBackupCookie.value;
      }
      
      let orderItems = [];
      for (const item of order.items) {
        orderItems.push(item.sku);
      }

      // Prepare the conversion data using the same structure as original
      const conversionData = {
        value: order.totalPriceAsNumber,
        currency: currency || order.currency,
        fbc: fbc || undefined, // Facebook click ID
        orderid: order.orderId,
        orderItems: orderItems,
        email: order.address.email,
      };
      
      console.log('Sending Facebook conversion:', conversionData);
      
      // Send to our server-side API endpoint
      const response = await $fetch('/api/facebook/capi', {
        method: 'POST',
        body: conversionData,
      });
      
      console.log('Facebook conversion response:', response);
      return response;
      
    } catch (error) {
      console.error('Failed to send Facebook conversion:', error);
      throw error;
    }
  };
  
  /**
   * Send custom Facebook conversion event
   */
  const sendCustomConversion = async(eventName: string, value: number, currency: string = 'SEK') => {
    try {
      const fbBackupCookie = useCookie<string>('fbBackupCookie', { 
        maxAge: 3600 * 24 * 182, 
        path: '/', 
      });
      
      const conversionData = {
        eventName,
        currency,
        value,
        fbc: fbBackupCookie.value || undefined,
      };
      
      console.log('Sending custom Facebook conversion:', conversionData);
      
      const response = await $fetch('/api/facebook/capi', {
        method: 'POST',
        body: conversionData,
      });
      
      console.log('Facebook conversion response:', response);
      return response;
      
    } catch (error) {
      console.error('Failed to send custom Facebook conversion:', error);
      throw error;
    }
  };
  
  return {
    sendPurchaseConversion,
    sendCustomConversion,
  };
}
